import { toast } from '@/components/toast';
import { getAudioResultUrl, PolotnoStoreActivePageKeys, useSetPolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { useCurrentProjectId } from '@/pages/AdvancedEditor/hooks/useCurrentProjectId';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { CacheStatus } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import useUploadVoice from '@/pages/editor/components/hooks/useUploadVoice';
import RecordAudioBox from '@/pages/editor/components/RecordAudioBox';
import { updateVideo } from '@/services/api';
import { useAuth } from '@/services/auth';
import { LibraryItemType } from '@d-id/types';
import { observer } from 'mobx-react-lite';
import { useQueryClient } from 'react-query';

interface Props {
    isUploading: boolean;
    setIsUploading: (isUploading: boolean) => void;
    setIsRecording: (isRecording: boolean) => void;
    onRecordComplete?: () => void;
    sceneId: string;
    updateSceneAudio: (audioData: AudioData | null) => void;
}

export const RecordAudioControl = observer(
    ({ isUploading, setIsUploading, setIsRecording, onRecordComplete, sceneId, updateSceneAudio }: Props) => {
        const queryClient = useQueryClient();
        const { user } = useAuth();
        const projectId = useCurrentProjectId();
        const setTtsAudioCacheStatus = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioCacheStatus);

        const { upload: uploadVoice } = useUploadVoice({
            resultUrl: getAudioResultUrl({
                projectId,
                user,
                pageId: sceneId,
            }),
            onUploadComplete: updateSceneAudio,
        });

        const handleRecordComplete = async (blob: Blob) => {
            setIsUploading(true);
            setTtsAudioCacheStatus(CacheStatus.Invalid);

            try {
                const { sourceUrl, duration } = await uploadVoice(undefined, blob);
                if (duration !== -1) {
                    const updateData = { audio_url: sourceUrl, duration };
                    await updateVideo(projectId, updateData, LibraryItemType.PROJECT);
                    await queryClient.invalidateQueries({ queryKey: [`get-project-${projectId}`] });
                }
            } catch {
                toast.error({ title: 'Failed to upload recorded audio' });
            }

            onRecordComplete?.();
            setIsUploading(false);
        };

        return <RecordAudioBox onRecordComplete={handleRecordComplete} isUploading={isUploading} setIsRecording={setIsRecording} />;
    }
);
